import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import { NumericOptionDropdown } from 'components/dropdown/NumericOptionDropdown';
import { AxesEnabledDisplay } from 'components/node_settings/AxesEnabledDisplay';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import {
    getDisabledNumberOfSamplesOptions,
    getDisabledSamplingFrequencyOptions,
    mergeDisabledOptions,
} from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/collectionDurationValidation';
import { LowFrequencyCollectionSetting } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/LowFrequencyCollectionSetting';
import {
    getDisabledPrimaryNumberOfSamplesOptions,
    getDisabledPrimarySamplingFrequencyOptions,
} from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/lowFrequencyCollectionValidation';
import { AxesEnabledValue } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/styled';
import { UpdateIntervalSetting } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/UpdateIntervalSetting';
import {
    DisabledHint,
    SectionDescription,
} from 'components/node_settings/V4EditSettingsModal/styled';
import { Setting } from 'components/node_settings/V4Settings/Setting';
import { SettingsList } from 'components/node_settings/V4Settings/styled';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import { V4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { ReactNode } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { getEnabledAxes, OptionChild } from 'utils/node';

type CollectionSettingsProps = Readonly<{
    description: ReactNode;
    samplingFrequencyOptions: OptionChild<number>[];
    numberOfSamplesOptions: OptionChild<number>[];
    updateIntervalOptions: OptionChild<number>[];
    onEditAxes?: () => void;
}>;

export function CollectionSettings({
    description,
    samplingFrequencyOptions,
    numberOfSamplesOptions,
    updateIntervalOptions,
    onEditAxes,
}: CollectionSettingsProps) {
    const { control } = useFormContext<V4SensorSettings>();
    const [
        vibrationX,
        vibrationY,
        vibrationZ,
        samplingFrequencyHz,
        numberOfSamples,
        updateInterval,
        lowFrequencyCollection,
    ] = useWatch({
        control,
        name: [
            'vibrationX',
            'vibrationY',
            'vibrationZ',
            'samplingFrequencyHz',
            'numberOfSamples',
            'updateInterval',
            'lowFrequencyCollection',
        ],
    });

    // Disable the options the API would reject on save (collection duration >= update interval).
    // Also disable a primary frequency that Low-Frequency Collection (when enabled) would no
    // longer be lower than, for the same reconciliation reason as the sample-count guard below.
    const samplingFrequencyDisabledOptions = mergeDisabledOptions(
        getDisabledSamplingFrequencyOptions(samplingFrequencyOptions, {
            numberOfSamples,
            updateInterval,
        }),
        getDisabledPrimarySamplingFrequencyOptions(
            samplingFrequencyOptions,
            lowFrequencyCollection,
        ),
    );
    const numberOfSamplesDisabledOptions = mergeDisabledOptions(
        getDisabledNumberOfSamplesOptions(numberOfSamplesOptions, {
            samplingFrequencyHz,
            updateInterval,
        }),
        getDisabledPrimaryNumberOfSamplesOptions(
            numberOfSamplesOptions,
            lowFrequencyCollection,
        ),
    );

    const { value: lowFrequencyCollectionEnabled } = useFeatureFlag(
        'v4SensorsLowFrequencyCollection',
    );

    // Every axis off makes this a temperature-only sensor, with no vibration to collect.
    const allAxesDisabled = !vibrationX && !vibrationY && !vibrationZ;

    return (
        <>
            <SectionDescription>{description}</SectionDescription>
            <SettingsList>
                <Setting name={V4_SETTINGS_TEXT.settingNames.axesEnabled}>
                    <AxesEnabledValue>
                        <AxesEnabledDisplay
                            enabledAxes={getEnabledAxes({
                                x: vibrationX,
                                y: vibrationY,
                                z: vibrationZ,
                            })}
                        />
                        {onEditAxes && (
                            <Button
                                appearance="link"
                                spacing="compact"
                                ariaLabel="Edit axes"
                                onClick={onEditAxes}
                            >
                                Edit <FontAwesomeIcon icon={faArrowRight} />
                            </Button>
                        )}
                    </AxesEnabledValue>
                </Setting>

                <UpdateIntervalSetting
                    updateIntervalOptions={updateIntervalOptions}
                    allAxesDisabled={allAxesDisabled}
                />

                <Setting name={V4_SETTINGS_TEXT.settingNames.samplingFrequency}>
                    {allAxesDisabled ? (
                        <DisabledHint>
                            {V4_SETTINGS_TEXT.disabledHints.allAxesDisabledHint}
                        </DisabledHint>
                    ) : (
                        <Controller
                            control={control}
                            name="samplingFrequencyHz"
                            render={({ field }) => (
                                <NumericOptionDropdown
                                    value={field.value}
                                    options={samplingFrequencyOptions}
                                    onChange={field.onChange}
                                    disabledOptions={samplingFrequencyDisabledOptions}
                                />
                            )}
                        />
                    )}
                </Setting>

                <Setting name={V4_SETTINGS_TEXT.settingNames.numberOfSamples}>
                    {allAxesDisabled ? (
                        <DisabledHint>
                            {V4_SETTINGS_TEXT.disabledHints.allAxesDisabledHint}
                        </DisabledHint>
                    ) : (
                        <Controller
                            control={control}
                            name="numberOfSamples"
                            render={({ field }) => (
                                <NumericOptionDropdown
                                    value={field.value}
                                    options={numberOfSamplesOptions}
                                    onChange={field.onChange}
                                    width="16rem"
                                    disabledOptions={numberOfSamplesDisabledOptions}
                                />
                            )}
                        />
                    )}
                </Setting>

                {lowFrequencyCollectionEnabled && (
                    <LowFrequencyCollectionSetting
                        allAxesDisabled={allAxesDisabled}
                        samplingFrequencyOptions={samplingFrequencyOptions}
                    />
                )}
            </SettingsList>
        </>
    );
}
